/// <reference types="chrome" />

import { Injectable } from '@angular/core';
import { mergeMap, Observable, of, take, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ChromeMessageExternal, ChromeMessageExternalFunction, ChromePrintMessage, ChromePrintMessageResponse, ChromePrintStatus, WebPrintRequestFunction, WebPrintSubmitRequest } from '../types/chrome-box-messages';
import { ChromePortName } from '../types/chrome-box-messages/chrome-port-name.enum';
import { PrintTicketBuilder } from '../utils/print-ticket-builder';

@Injectable({
    providedIn: 'root'
})

export class ChromeBoxMessagesService {

    get runtime(): typeof chrome.runtime {
        return window.chrome?.runtime;
    }

    constructor() { }

    private ab2str(document: ArrayBuffer): string {
        const bytes = new Uint8Array(document);
        let binary = '';
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        const base64 = btoa(binary);

        return base64;
    }

    isAvailable(): boolean {
        return !!(environment.kiosk_app_id && this.runtime);
    }

    getDeviceUUID(): Observable<string> {
        return new Observable<string>(subscriber => {
            if (!this.isAvailable()) {
                subscriber.next('');
                subscriber.complete();
            } else {
                const request: ChromeMessageExternal = {
                    type: ChromeMessageExternalFunction.UUID,
                };

                this.runtime.sendMessage<ChromeMessageExternal, {
                    error: string;
                    response: string;
                }>(
                    environment.kiosk_app_id,
                    request,
                    response => {
                        subscriber.next(response?.response || '');
                        subscriber.complete();
                    }
                );
            }

            return {
                unsubscribe: () => { }
            };
        })
            .pipe(
                timeout({ each: 20000, with: () => of('') })
            );
    }

    print(
        document: ArrayBuffer,
        printer: chrome.printing.Printer,
        printerInfo: chrome.printing.GetPrinterInfoResponse | null
    ): Observable<ChromePrintMessageResponse> {
        return new Observable<ChromePrintMessageResponse>(subscriber => {
            let port: chrome.runtime.Port;

            if (!this.isAvailable()) {
                subscriber.next({
                    status: ChromePrintStatus.NO_CHROMEBOX,
                    message: `Chrome context not available`,
                    finalized: true
                });
                subscriber.complete();
            } else {
                const request: WebPrintSubmitRequest = {
                    type: WebPrintRequestFunction.SUBMIT,
                    job: {
                        printerName: printer.name,
                        document: this.ab2str(document),
                        ticket: PrintTicketBuilder.build(printer, printerInfo),
                    }
                };

                port = this.runtime.connect(<string>environment.kiosk_app_id, {
                    name: ChromePortName.PRINT,
                });

                port.postMessage(request);

                port.onMessage.addListener((message: ChromePrintMessage) => {
                    const response = message.response;

                    subscriber.next(response);

                    if (response.finalized) {
                        subscriber.complete();
                    }
                });
            }

            return {
                unsubscribe: () => {
                    if (port) {
                        port.disconnect();
                    }
                }
            };
        })
            .pipe(
                timeout({
                    each: 60000,
                    with: () => of({
                        status: ChromePrintStatus.TIMEOUT,
                        message: `Chrome context not responding`,
                        finalized: true
                    })
                })
            );
    }

    printByPrinterName(document: ArrayBuffer, printerName: string): Observable<ChromePrintMessageResponse> {
        return this.listPrinters()
            .pipe(
                take(1),
                mergeMap(printers => {
                    const printer = printers.find(printer =>
                        printer.name === printerName
                    );

                    if (!printer) {
                        const printerNames = printers.map((printer) => printer.name).join(',');

                        return of({
                            status: ChromePrintStatus.PRINTER_NOT_FOUND,
                            message: `printer: ${printerName} not found or connected, printer name list: ${printerNames}`,
                            finalized: true
                        });
                    }

                    return this.getPrinterInfo(printer.id)
                        .pipe(
                            take(1),
                            mergeMap(printerInfo =>
                                this.print(document, printer, printerInfo)
                            )
                        );
                })
            );
    }

    listPrinters(): Observable<chrome.printing.Printer[]> {
        return new Observable<chrome.printing.Printer[]>((subscriber) => {
            if (!this.isAvailable()) {
                subscriber.next([]);
                subscriber.complete();
            } else {
                const request: ChromeMessageExternal = {
                    type: ChromeMessageExternalFunction.LIST_PRINTERS,
                };
                this.runtime.sendMessage<ChromeMessageExternal, chrome.printing.Printer[]>(
                    environment.kiosk_app_id,
                    request,
                    (response) => {
                        subscriber.next(response || []);
                        subscriber.complete();
                    }
                );
            }

            return {
                unsubscribe: () => { },
            };
        }).pipe(timeout({ each: 20000, with: () => of([]) }));
    }

    getPrinterInfo(
        printerId: string
    ): Observable<chrome.printing.GetPrinterInfoResponse | null> {
        return new Observable<chrome.printing.GetPrinterInfoResponse | null>(
            (subscriber) => {
                if (!this.isAvailable()) {
                    subscriber.next(null);
                    subscriber.complete();
                } else {
                    const request: ChromeMessageExternal = {
                        type: ChromeMessageExternalFunction.GET_PRINTER_INFO,
                        printerId,
                    };

                    this.runtime.sendMessage<
                        ChromeMessageExternal,
                        chrome.printing.GetPrinterInfoResponse | null
                    >(
                        environment.kiosk_app_id,
                        request,
                        (response) => {
                            subscriber.next(response || null);
                            subscriber.complete();
                        }
                    );
                }

                return {
                    unsubscribe: () => { },
                };
            }
        ).pipe(timeout({ each: 60000, with: () => of(null) }));
    }
}
